import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Search, Phone, Mail, MessageCircle, StickyNote, MessageSquare, Send, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ELEVATED = ["admin", "dept_lead"];
const TYPES = [
  { value: "", label: "All types" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "note", label: "Note" },
];

const TYPE_META = {
  call: { icon: Phone, tone: "text-blue-500 bg-blue-500/10", label: "Call" },
  email: { icon: Mail, tone: "text-purple-500 bg-purple-500/10", label: "Email" },
  whatsapp: { icon: MessageCircle, tone: "text-emerald-500 bg-emerald-500/10", label: "WhatsApp" },
  note: { icon: StickyNote, tone: "text-amber-500 bg-amber-500/10", label: "Note" },
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function CommRow({ r }) {
  const meta = TYPE_META[r.type] || TYPE_META.note;
  const Icon = meta.icon;
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [count, setCount] = useState(r.comment_count || 0);

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/communications/${r.id}/comments`);
      setComments(data);
      setCount(data.length);
    } catch { setComments([]); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && comments === null) await loadComments();
  };

  const submit = async () => {
    const body = reply.trim();
    if (!body) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/communications/${r.id}/comments`, { body });
      setComments((prev) => [...(prev || []), data]);
      setCount((c) => c + 1);
      setReply("");
    } catch (e) { toast.error("Failed to post comment"); }
    setPosting(false);
  };

  return (
    <li data-testid={`comm-row-${r.id}`} className="px-5 py-4 hover:bg-secondary/20 transition-colors">
      <div className="flex gap-4">
        <div className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${meta.tone}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Link
              to={`/affiliates/${r.affiliate_id}`}
              className="text-sm font-semibold hover:text-primary transition-colors"
              data-testid={`comm-aff-link-${r.id}`}
            >
              {r.affiliate_name || "—"}
            </Link>
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{meta.label}</span>
            <span className="text-[11px] text-muted-foreground ml-auto">{formatWhen(r.created_at)}</span>
          </div>
          <div className="text-sm text-foreground/90 mt-1 whitespace-pre-wrap break-words">{r.body}</div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span>by <span className="font-medium">{r.by_user_name || "—"}</span></span>
            <button
              data-testid={`comm-thread-toggle-${r.id}`}
              onClick={toggle}
              className="inline-flex items-center gap-1 hover:text-foreground transition-colors font-medium"
            >
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <MessageSquare className="h-3 w-3" />
              {count === 0 ? "Comment" : `${count} comment${count === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="mt-3 ml-14 pl-4 border-l-2 border-border/50 space-y-3">
          {comments === null ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No comments yet — be the first.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} data-testid={`comm-comment-${c.id}`} className="text-sm">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold">{c.by_user_name}</span>
                  <span className="text-[10px] text-muted-foreground">{formatWhen(c.created_at)}</span>
                </div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{c.body}</div>
              </div>
            ))
          )}
          <div className="flex gap-2 pt-1">
            <textarea
              data-testid={`comm-reply-input-${r.id}`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
              }}
              placeholder="Add a comment…"
              rows={2}
              className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              data-testid={`comm-reply-submit-${r.id}`}
              onClick={submit}
              disabled={posting || !reply.trim()}
              className="rounded-xl bg-primary text-primary-foreground text-xs font-semibold px-4 hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center gap-1.5"
            >
              <Send className="h-3.5 w-3.5" />
              {posting ? "Posting…" : "Post"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function Communications() {
  const { user } = useAuth();
  const isElevated = user && ELEVATED.includes(user.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [byUserId, setByUserId] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (isElevated) {
      api.get("/users").then((r) => setTeamMembers(r.data));
    }
  }, [isElevated]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (type) params.type = type;
    if (q) params.q = q;
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
    if (byUserId) params.by_user_id = byUserId;
    api.get("/communications/all", { params })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [type, q, dateFrom, dateTo, byUserId]);

  const counts = useMemo(() => {
    const c = { call: 0, email: 0, whatsapp: 0, note: 0 };
    for (const r of rows) if (c[r.type] !== undefined) c[r.type] += 1;
    return c;
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Communications"
        subtitle={
          isElevated
            ? `All logged conversations across the team`
            : `Conversations logged for your affiliates`
        }
      />

      <div className="p-6 md:p-10">
        {/* Type summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Object.entries(TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <div
                key={key}
                data-testid={`comm-summary-${key}`}
                className="rounded-2xl border border-border/60 bg-card px-4 py-3 flex items-center gap-3"
              >
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${meta.tone}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{meta.label}</div>
                  <div className="text-lg font-bold leading-tight">{counts[key]}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="comm-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search in message…"
              className="pl-9 h-10 rounded-full w-72"
            />
          </div>
          <select
            data-testid="comm-type-filter"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
          >
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {isElevated && (
            <select
              data-testid="comm-user-filter"
              value={byUserId}
              onChange={(e) => setByUserId(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
            >
              <option value="">All team members</option>
              {teamMembers.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          <Input
            data-testid="comm-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-10 rounded-full w-40"
          />
          <Input
            data-testid="comm-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-10 rounded-full w-40"
          />
          {(type || q || dateFrom || dateTo || byUserId) && (
            <button
              data-testid="comm-clear-filters"
              onClick={() => { setType(""); setQ(""); setDateFrom(""); setDateTo(""); setByUserId(""); }}
              className="h-10 rounded-full px-4 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table / feed */}
        <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading communications…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="comm-empty">
              No communications match your filters.
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {rows.map((r) => (
                <CommRow key={r.id} r={r} />
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground">
          Showing {rows.length} entr{rows.length === 1 ? "y" : "ies"}
          {rows.length === 500 && " (list capped — narrow filters to see more)"}
        </div>
      </div>
    </div>
  );
}
